import React, { Component } from 'react';
import classNames from 'classnames';
import { network } from '@tencent/netcheck';
import Divider from '@tencent/dui/lib/components/Divider';
import DuiDropdown from '@tencent/dui/lib/components/Dropdown';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import Menu from '@tencent/dui/lib/components/Menu';
import {
    isDocAttachmentElement,
    isAudioAttachmentElement, isVideoElement,
    isUnknownAttachmentElement,
} from '@src/slide/domain/ui-model/elements/element-judge';
import i18n from '@i18next/i18n';
import AttachmentUploader, { FILE_TYPE } from '@src/js/common/ark-extensions/dc-components/attachment-uploader';
import {
    TOOLBAR_DOWNLOAD_ATTACHMENT_ICON,
    TOOLBAR_ICON_TEXT_ICON,
    TOOLBAR_ATTACHMENT_DELETE,
    TOOLBAR_DROPDOWN,
    TOOLBAR_ICON_WITH_TEXT_ICON,
    TOOLBAR_ICON_CARD_ICON,
    TOOLBAR_PREVIEW_ICON,
    MUSIC_ATTACHMENT_PREVIEW_ICON,
    TOOLBAR_PICTURE, TOOLBAR_PIC_RESTORE,
} from '@src/framework/editor/src/img/pageIndex';
import styles from './index.less';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AttachmentSwitcher } from '@src/slide/application/service/attachment-upload/attachment-switcher';
import {
    CARD_CONFIG, EChangePosterType, EResetPosterType,
    EShowType,
    FROM_CLIPBOARD_CONFIG,
    FROM_CUR_FRAME_CONFIG,
    FROM_LOCAL_FILE_CONFIG,
    ICON_TEXT_CONFIG,
    PREVIEW_CONFIG, RESET_POSTER_SELF_CONFIG, RESET_POSTER_SIZE_CONFIG,
    SIMPLE_TEXT_CONFIG,
} from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';
import { getAttachmentShowType, getAttachmentObjKey, getAttachmentInfo } from '@src/slide/domain/ui-model/element-modeles/property/attachment/utils/attachment';
import type { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import type { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { PPTEditorStateType, PPTEditorStateTypeValue } from '@src/slide/domain/ui-model/constant/edit-state-type';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ISnackbarProps } from '@tencent/dui/lib/components/Snackbar/interface';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import {
    ImageReplaceAction,
} from '@src/slide/application/service/copy-paste/core/cp-image.new/image-edit-manager/core/image-edit-manager.interface';
import ToolbarItemImage from '@com/tool-bar/toolbar-item-image';
import * as FloatBarMenu from '@editor/src/js/constants/float-toobbar-data';
import { MediaRegion } from '@src/slide/application/service/media-manager/media-manager.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { getAllChildModels } from '@src/slide/domain/ui-model/element-modeles/property/utils/merge-property';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { disposeOnUnmount } from 'mobx-react';
import { reaction } from 'mobx';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { handleModelMenuEvent } from '@src/slide/application/service/menu-manager/menu-event';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { EStatusEvents } from '@src/js/common/ark-extensions/dc-components/attachment-uploader/type';

export interface NormalAttachmentToolbarProps {
    selectedModel: IPictureModel | IShapeModel;
    shadow?: boolean;
    pptEditorState: PPTEditorStateTypeValue;
}
interface MenuItem {
    icon: any;
    id: EShowType;
    title: string;
}
interface PosterItem {
    id: EChangePosterType;
    title: string;
}
interface RestPosterItem {
    id: EResetPosterType;
    title: string;
}
type MenuData = Array<MenuItem>;
type PosterData = Array<PosterItem>;
type ResetPosterData = Array<RestPosterItem>;

// 附件工具栏选项类型
enum ToolbarItemType {
    preview = 'preview',
    clip = 'clip',  // 裁剪，目前只有视频附件有此选项
    changePoster = 'changePoster', // 切换封面，目前只有视频附件有此选项
    resetPoster = 'resetPoster', // 重置封面，目前只有视频附件有此选项
    download = 'download',
    switch = 'switch',
    delete = 'delete',
    divider = 'divider'
}

export default class NormalAttachmentToolbar extends Component<NormalAttachmentToolbarProps> {
    private snackbarOp?: ReturnType<typeof Snackbar.show>|null;

    public render(): JSX.Element | null {
        const { selectedModel } = this.props;
        if (!selectedModel) {
            return null;
        }
        const attachmentManager = selectedModel.getAttachmentManager();
        if (this.isUploading() || attachmentManager?.isUploadError(selectedModel.id as string)) {
            return null;
        }
        const itemClass = classNames({
            [styles['attachment-toolbar']]: true,
            [styles['attachment-toolbar-shadow']]: this.props.shadow,
            'attachment-video-toolbar': isVideoElement(selectedModel), // 用于 e2e 查找到 video toolbar
        });
        const itemConfig = this.getItemConfig();
        if (!itemConfig.length) {
            return null;
        }
        return (
            <div className={itemClass}>
                {
                    itemConfig.map(({ type }) => {
                        switch (type) {
                            case ToolbarItemType.preview:
                                return this.getPreviewItem();
                            case ToolbarItemType.clip:
                                return this.getClipItem();
                            case ToolbarItemType.changePoster:
                                return this.getPosterFrameItem();
                            case ToolbarItemType.resetPoster:
                                return this.getResetPosterItem();
                            case ToolbarItemType.download:
                                return this.getDownloadItem();
                            case ToolbarItemType.switch:
                                return this.getSwitchItem();
                            case ToolbarItemType.delete:
                                return this.getDeleteItem();
                            case ToolbarItemType.divider:
                                return (
                                    <Divider orientation="vertical" />
                                );
                            default:
                                return null;
                        }
                    })
                }
            </div>
        );
    }

    public async componentDidMount() {
        const { selectedModel } = this.props;
        const mediaManager = selectedModel.getMediaManager();
        if (!mediaManager) return;
        const attachmentManager = selectedModel.getAttachmentManager();
        if (!attachmentManager) return;

        /*  更新 isVideoUploading */
        disposeOnUnmount(this, reaction(
            () => attachmentManager?.attachmentSuccessHappen,
            () => {
                if (isVideoElement(selectedModel)) {
                    this.forceUpdate();
                }
            },
        ));

        disposeOnUnmount(this, reaction(
            () => mediaManager?.getVideoInstanceByRegion(selectedModel.key, MediaRegion.EDITOR)
                && mediaManager?.shouldShowMediaElement(
                    selectedModel.key,
                    selectedModel.id as string,
                    MediaRegion.EDITOR,
                ),
            () => {
                this.forceUpdate();
            },
        ));

        const module = await this.getCopyPasteImageService()?.promise();
        disposeOnUnmount(this, reaction(
            () => module?.ImageEditManager.getInstance().clipDataWaiting,
            (waiting) => {
                this.handleImageReplace(waiting);
            },
        ));
    }

    // eslint-disable-next-line complexity
    private getItemConfig = () => {
        const { selectedModel, pptEditorState } = this.props;
        if (!selectedModel) {
            return [];
        }
        const attachmentManager = selectedModel.getAttachmentManager();
        // 桌面端本地编辑场景暂时不支持 附件工具栏其他选项
        if (ENV.IS_LOCAL_EDIT) {
            return [{ type: ToolbarItemType.delete }];
        }

        const ifRenderSwitch = !(
            this.isVideoUploading()
            || attachmentManager?.getUploadStatus(selectedModel.id as string) === EStatusEvents.ERROR
        );

        if (pptEditorState === PPTEditorStateType.EDITABLE) {
            const isVideo = isVideoElement(selectedModel);
            return [
                { type: ToolbarItemType.preview },
                { type: ToolbarItemType.divider },
                { type: isVideo ? ToolbarItemType.clip : null },
                { type: isVideo ? ToolbarItemType.divider : null },
                { type: isVideo ? ToolbarItemType.changePoster : null },
                { type: isVideo ? ToolbarItemType.divider : null },
                { type: isVideo ? ToolbarItemType.resetPoster : null },
                { type: isVideo ? ToolbarItemType.divider : null },
                { type: ToolbarItemType.download },
                { type: ToolbarItemType.divider },
                { type: ifRenderSwitch ? ToolbarItemType.switch : null },
                { type: ifRenderSwitch ? ToolbarItemType.divider : null },
                { type: ToolbarItemType.delete },
            ];
        }
        if (pptEditorState === PPTEditorStateType.COPYABLE) {
            // 可以查看附件和下载附件
            return [
                { type: ToolbarItemType.preview },
                { type: ToolbarItemType.divider },
                { type: ToolbarItemType.download },
            ];
        }
        if (pptEditorState === PPTEditorStateType.SELECTABLE) {
            // 只能查看附件
            return [
                { type: ToolbarItemType.preview },
            ];
        }
        return [];
    };

    /**
     * 获取类型切换列表项
     * @returns
     */
    private getMenuData = (): MenuData => {
        const items = [
            {
                ...SIMPLE_TEXT_CONFIG,
                icon: TOOLBAR_ICON_TEXT_ICON,
            },
            {
                ...ICON_TEXT_CONFIG,
                icon: TOOLBAR_ICON_WITH_TEXT_ICON,
            },
            {
                ...CARD_CONFIG,
                icon: TOOLBAR_ICON_CARD_ICON,
            },
        ];
        const { selectedModel } = this.props;
        if (isDocAttachmentElement(selectedModel)) {
            return items;
        }
        if (isAudioAttachmentElement(selectedModel)) {
            return [
                ...items,
                {
                    ...PREVIEW_CONFIG,
                    icon: MUSIC_ATTACHMENT_PREVIEW_ICON,
                },
            ];
        }
        if (isUnknownAttachmentElement(selectedModel)) {
            return [items[0]];
        }

        return [
            ...items,
            {
                ...PREVIEW_CONFIG,
                icon: TOOLBAR_PREVIEW_ICON,
            },
        ];
    };

    /**
     * 获取当前选中的数据
     * @param menuData
     * @returns
     */
    private getSelectedItem = (menuData: MenuData): MenuItem => {
        const { selectedModel } = this.props;
        const showType = getAttachmentShowType(selectedModel);
        return menuData.find(({ id }) => id === showType) || menuData[0];
    };

    /**
     * 获取更换封面列表项（目前只有视频附件支持）
     * @returns
     */
    private getPosterData = (): PosterData => {
        const items = [FROM_LOCAL_FILE_CONFIG, FROM_CLIPBOARD_CONFIG];
        const { selectedModel } = this.props;
        if (isVideoElement(selectedModel)) {
            items.push(FROM_CUR_FRAME_CONFIG);
        }
        return items;
    };

    /**
     * 是否能使用视频当前帧设置成封面图（目前只有视频附件支持）
     * @returns
     */
    private canChangePosterFromCurFrame = () => {
        const { selectedModel } = this.props;
        const mediaManager = selectedModel.getMediaManager();
        if (!mediaManager) {
            return false;
        }
        const player = mediaManager?.getVideoInstanceByRegion(selectedModel.key, MediaRegion.EDITOR);
        const shouldShow = mediaManager?.shouldShowMediaElement(
            selectedModel.key,
            selectedModel.id as string,
            MediaRegion.EDITOR,
        );
        return player && shouldShow;
    };

    /**
     * 获取重置封面列表项（目前只有视频附件支持）
     * @returns
     */
    private getRestPosterData = (): ResetPosterData => [RESET_POSTER_SELF_CONFIG, RESET_POSTER_SIZE_CONFIG];

    private isUploading = () => {
        const { selectedModel } = this.props;
        if (!selectedModel) {
            return false;
        }
        const uploadingMap = selectedModel.getAttachmentManager()?.uploadingMap;
        return uploadingMap?.has(selectedModel.props?.attachment?.id);
    };

    private isVideoUploading = () => {
        const { selectedModel } = this.props;
        if (!selectedModel) {
            return false;
        }
        const attachmentManager = selectedModel.getAttachmentManager();
        return attachmentManager?.isVideoElementUploading(selectedModel as IPictureModel);
    };

    private handleVideoPosterChange = async (id: EChangePosterType) => {
        switch (id) {
            case EChangePosterType.FROM_CLIPBOARD:
            {
                const imageEditManager = await this.getImageEditManager();
                imageEditManager?.replaceImageFromClipBoard(ImageReplaceAction.foreground);
                break;
            }
            case EChangePosterType.FROM_LOCAL_FILE:
            {
                const imageEditManager = await this.getImageEditManager();
                imageEditManager?.replaceImageFromFile(
                    ImageReplaceAction.foreground,
                    // 视频附件封面暂时不支持 gif
                    FILE_TYPE.image.filter(type => type !== 'gif').map(format => `image/${format}`)
                        .join(', '),
                );
                break;
            }
            case EChangePosterType.FROM_CUR_FRAME:
                this.changePosterFromCurVideoFrame();
                break;
        }
    };

    private async handleImageReplace(waiting) {
        const module = await this.getCopyPasteImageService()?.promise();
        if (!module) return;
        const { ImageEditManager } = module;
        if (ImageEditManager.getInstance().imageReplaceAction === 2) return; // 表示是填充图片
        if (waiting) {
            this.snackbarOp = Snackbar.show({
                message: browser.isMac ? i18n.t('请使用 Cmd+V 替换该图片') : i18n.t('请使用 Ctrl+V 替换该图片'),
                duration: 3000,
            });
        } else if (this.snackbarOp) {
            this.snackbarOp.close();
            this.snackbarOp = null;
        }
    }

    private changePosterFromCurVideoFrame = () => {
        const { selectedModel } = this.props;
        const player = selectedModel.getMediaManager()?.getVideoInstanceByRegion(selectedModel.key, MediaRegion.EDITOR);
        if (player) {
            this.captureVideo(player)?.then(async (blob) => {
                if (blob) {
                    // 参考替换图片逻辑
                    const imageEditManager = await this.getImageEditManager();
                    if (!imageEditManager) return;
                    const images = await imageEditManager?.createEditorImages([blob]);
                    // 设置 imageEditManager 状态
                    imageEditManager.imageReplaceAction = ImageReplaceAction.foreground;
                    imageEditManager.imageReplacing = true;
                    imageEditManager.clipDataWaiting = false;
                    if (images?.length) {
                        const imageUploadService = await this.getImageUploadService()?.promise();
                        imageUploadService?.handleImageChange(images);
                    }
                }
            });
        }
    };

    // player.captureVideo 方法捕获不到最后一帧的数据, 因此这里自定义方法来实现
    private captureVideo(player: any): Promise<Blob> | undefined {
        const { currentVideoInfo: { videoWidth, videoHeight }, videoElements } = player;
        if (!videoElements?.length) {
            return;
        }
        const video = videoElements.find(ele => ele.hasAttribute('src'));
        if (!(video instanceof HTMLVideoElement)) {
            return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = videoWidth;
        canvas.height = videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        ctx.drawImage(video, 0, 0, videoWidth, videoHeight);

        return new Promise((resolve) => {
            canvas.toBlob((res) => {
                res && resolve(res);
            }, 'image/jpeg', 1.0);
        });
    }

    private handleVideoPosterReset = (id: EResetPosterType) => {
        transaction(() => {
            const { selectedModel } = this.props;
            const selectedModels = getAllChildModels(selectedModel);
            selectedModels.forEach((model) => {
                if (model.type === UIModelType.IMAGE) {
                    (model as IPictureModel).handleResetPicture(id === EResetPosterType.RESET_POSTER_SIZE);
                } else if (model.type === UIModelType.SHAPE) {
                    (model as IShapeModel).getImageFillEffect()?.handleResetPicture();
                }
            });
        });
    };

    private getCopyPasteImageService() {
        return this.getAppServices().editableServiceLayer?.copyPasteImageService;
    }

    private async getImageEditManager() {
        const module = await this.getCopyPasteImageService()?.promise();
        if (!module) return;
        const { ImageEditManager } = module;
        return ImageEditManager.getInstance();
    }

    private getImageUploadService() {
        return this.getAppServices()?.editableServiceLayer?.imageUploadService;
    }

    private handleDownload = () => {
        const { selectedModel } = this.props;
        if (!network.isOnline) return;
        const objKey = getAttachmentObjKey(selectedModel);
        // 视频文件支持直接下载
        if (isVideoElement(selectedModel)) {
            const attachmentManager = selectedModel.getAttachmentManager();
            const attachmentId = attachmentManager?.getLocalAttachment(selectedModel.id as string)?.id;
            AttachmentUploader.getInstance().download(objKey as string, attachmentId);
        } else {
            if (this.isUploading()) return;
            if (objKey) {
                AttachmentUploader.getInstance().download(objKey);
            }
        }
    };

    private handleDelete = () => {
        this.getSelectionService().currentSlide?.deleteElement();
    };

    /**
     * 查看附件
     * @returns
     */
    private handlePreview = () => {
        if (!network.isOnline || this.isUploading()) {
            return;
        }
        const { selectedModel } = this.props;
        const attachmentInfo = getAttachmentInfo(selectedModel);
        attachmentInfo && AttachmentUploader.getInstance().viewAttachmentInfo(attachmentInfo);
    };

    private getSelectionService() {
        const appService = AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
        return appService.viewableServiceLayer.selectionService;
    }

    private handleTypeChange = (id: EShowType) => {
        const { selectedModel } = this.props;
        AttachmentSwitcher.switch(selectedModel, id, (message: string, type: ISnackbarProps['type']) => {
            Snackbar.show({ message, type, duration: 3000 });
        });
        this.forceUpdate();
    };

    private getPreviewItem = () => (
        <div
            className={`${styles['attachment-toolbar-text-item']} ${network.isOnline ? '' : styles.disabled}`}
            onClick={this.handlePreview}
        >
            {i18n.t('查看附件')}
        </div>
    );

    private getClipItem = () => {
        // 暂时依赖 quickSetMenu 中已有的裁剪功能
        const clipItem = FloatBarMenu.ImageQuickItems.find(item => item.id === ActionType.TAILOR_PIC);
        const RenderComponent: any = ToolbarItemImage;
        const { selectedModel } = this.props;

        return (<RenderComponent
            key={'video-clip-toolbar'}
            {...{
                ...clipItem,
                handleClick: (id) => {
                    handleModelMenuEvent(selectedModel, { menuType: MenuType.QUICK_SETTING, actionType: id });
                },
                dropdown: {
                    ...clipItem?.dropdown,
                },
            }}
            model='quick-setting-model'
        />);
    };

    private getPosterFrameItem = () => {
        const posterData = this.getPosterData();

        return (
            <DuiDropdown
                dropContent={
                    <Menu
                        onClick={this.handleVideoPosterChange}>
                        {
                            posterData.map(({ id, title }) => {
                                const disabled = id === EChangePosterType.FROM_CUR_FRAME
                                    ? !this.canChangePosterFromCurFrame()
                                    : false;
                                return (
                                    <Menu.Item
                                        style={{ paddingLeft: '12px', paddingRight: '12px' }}
                                        id={id}
                                        key={id}
                                        disabled={disabled}>
                                        {title}
                                    </Menu.Item>
                                );
                            })
                        }
                    </Menu>
                }
            >
                <Tooltip title={i18n.t('更换封面')} trigger="hover" placement="top" hideOnClick>
                    <div className={`${styles['attachment-toolbar-switch-mode']} ${styles['attachment-toolbar-item']}`}>
                        <img
                            src={TOOLBAR_PICTURE}
                            className={styles.icon}
                        />
                        <img src={TOOLBAR_DROPDOWN} className={styles['dropdown-icon']} />
                    </div>
                </Tooltip>
            </DuiDropdown
            >
        );
    };

    private getResetPosterItem = () => {
        const resetPosterData = this.getRestPosterData();

        return (
            <DuiDropdown
                dropContent={
                    <Menu
                        onClick={this.handleVideoPosterReset}>
                        {
                            resetPosterData.map(({ id, title }) => (
                                <Menu.Item style={{ paddingLeft: '12px', paddingRight: '12px' }} id={id} key={id}>{title}</Menu.Item>
                            ))
                        }
                    </Menu>
                }
            >
                <Tooltip title={i18n.t('重置封面')} trigger="hover" placement="top" hideOnClick>
                    <div className={`${styles['attachment-toolbar-switch-mode']} ${styles['attachment-toolbar-item']}`}>
                        <img
                            src={TOOLBAR_PIC_RESTORE}
                            className={styles.icon}
                        />
                        <img src={TOOLBAR_DROPDOWN} className={styles['dropdown-icon']} />
                    </div>
                </Tooltip>
            </DuiDropdown
            >
        );
    };

    private getDownloadItem = () => (
        <Tooltip title={i18n.t('下载附件')} trigger="hover" placement="top" hideOnClick>
            <img
                src={TOOLBAR_DOWNLOAD_ATTACHMENT_ICON}
                onClick={this.handleDownload}
                className={`${styles['attachment-toolbar-item']}  ${styles.icon} ${
                    network.isOnline && SlideDataServices.PrivilegeManager()?.copyable ? '' : styles.disabled
                }`}
            />
        </Tooltip>
    );

    private getSwitchItem = () => {
        const menuData = this.getMenuData();
        const selectedItem = this.getSelectedItem(menuData);
        return (
            <DuiDropdown
                disabled={!network.isOnline}
                dropContent={
                    <Menu
                        onClick={this.handleTypeChange}
                        selectedIds={[selectedItem.id]}
                        itemStyle={{ width: '166px' }}
                        type="with-icon"
                    >
                        {
                            menuData.map(({ id, icon, title }) => (
                                <Menu.Item
                                    id={id}
                                    key={id}
                                    icon={icon}
                                >
                                    {title}
                                </Menu.Item>
                            ))
                        }
                    </Menu>
                }
            >
                <Tooltip title={i18n.t('切换显示方式')} trigger="hover" placement="top" hideOnClick>
                    <div className={`${styles['attachment-toolbar-switch-mode']} ${styles['attachment-toolbar-item']}`}>
                        <img
                            src={selectedItem.icon}
                            className={styles.icon}
                        />
                        <img src={TOOLBAR_DROPDOWN} className={styles['dropdown-icon']}/>
                    </div>
                </Tooltip>
            </DuiDropdown>
        );
    };

    private getDeleteItem = () => (
        <Tooltip title={i18n.t('删除')} trigger="hover" placement="top" hideOnClick>
            <img
                src={TOOLBAR_ATTACHMENT_DELETE}
                className={`${styles['attachment-toolbar-item']}  ${styles.icon}`}
                onClick={this.handleDelete}
            />
        </Tooltip>
    );

    private getAppServices() {
        return AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
    }
}
